import { useMemo, useState, useEffect, useRef } from 'react'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useFetchResponse, useReselector } from '@/utils/sharedHooks'

import { Card, Text, Link, Icon, CardColors, Loader } from '@/ui'
import {
  FETCH_INSIGHTS_SUMMARY_KEY,
  fetchInsightsSummary,
} from '../../shared/financeActions'
import {
  INSIGHTS_TYPE,
  formatSummary,
  formatPercentage,
} from '../../shared/utils'
import {
  faArrowUpRight,
  faArrowDownRight,
} from '@fortawesome/pro-regular-svg-icons'
import { selectInsightSummary } from '@/reducers/insights.slice'
import { TextColors, TypographyStyles } from '@/styles'

export interface InsightOverviewCardProps {
  headerText: string
  headerColor?: keyof typeof TextColors
  type: string
  percentChange?: number
  month?: number
  year?: number
}

const getPercentChangeDescription = (percentChange = 0) => {
  const formattedPercentChange = formatPercentage(percentChange)
  if (percentChange > 0) {
    return `${formattedPercentChange} higher than last month`
  } else if (percentChange < 0) {
    return `${formattedPercentChange} lower than last month`
  } else {
    return ''
  }
}

const getIcon = (percentChange = 0) => {
  if (percentChange > 0) {
    return <Icon icon={faArrowUpRight} className="mr-2" />
  } else if (percentChange < 0) {
    return <Icon icon={faArrowDownRight} className="mr-2" />
  }
  return null
}

export const InsightOverviewCard = ({
  headerText,
  type,
  headerColor,
  percentChange,
  month,
  year,
}: InsightOverviewCardProps) => {
  const insightsLink = useMemo(() => {
    let baseLink = `/insights/${type.toLowerCase()}?`
    if (month) {
      baseLink += `month=${month - 1}&`
    }
    if (year) {
      baseLink += `year=${year}`
    }
    return baseLink
  }, [type, month, year])

  const trackMessage = `view-${type}-breakdown`

  return (
    <Card color={CardColors.LEVEL_1} className="flex flex-col my-0">
      <Text
        variant={TypographyStyles.DISPLAY_MD}
        color={headerColor as TextColors}
        className="whitespace-nowrap"
      >
        {headerText}
      </Text>
      <Text
        variant={TypographyStyles.LABEL_SM}
        color={TextColors.MUTED}
        className="mb-4"
      >
        {type}
      </Text>
      <div className="flex items-center mb-4">
        {getIcon(percentChange)}
        <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
          {getPercentChangeDescription(percentChange)}
        </Text>
      </div>
      <Link
        href={insightsLink}
        trackMessage={trackMessage}
        trackAttributes={{ screen: 'practice insights' }}
        className="mt-auto"
      >
        View {type} breakdown →
      </Link>
    </Card>
  )
}

export const InsightOverviewCards = ({
  year,
  month,
}: {
  year: number
  month: number
}) => {
  const dispatch = useAppDispatch()
  const [isLoading, setIsLoading] = useState(true)
  useEffect(() => {
    dispatch(fetchInsightsSummary(INSIGHTS_TYPE.INCOME, month, year))
  }, [dispatch, month, year])

  const incomeSummaryResponse = useReselector(
    selectInsightSummary,
    FETCH_INSIGHTS_SUMMARY_KEY(INSIGHTS_TYPE.INCOME, month, year)
  )

  const expenseSummaryResponse = useFetchResponse(
    fetchInsightsSummary,
    INSIGHTS_TYPE.EXPENSES,
    month,
    year
  )

  const profitSummaryResponse = useFetchResponse(
    fetchInsightsSummary,
    INSIGHTS_TYPE.PROFIT,
    month,
    year
  )

  const timeoutRef = useRef<NodeJS.Timeout>()

  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      setIsLoading(false)
    }, 5000)
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [year, month])

  useEffect(() => {
    if (
      incomeSummaryResponse ||
      expenseSummaryResponse ||
      profitSummaryResponse
    ) {
      setIsLoading(false)
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [incomeSummaryResponse, expenseSummaryResponse, profitSummaryResponse])

  const formattedIncome = useMemo(() => {
    return formatSummary(incomeSummaryResponse?.[0], INSIGHTS_TYPE.INCOME)
  }, [incomeSummaryResponse])

  const formattedExpense = useMemo(() => {
    return formatSummary(expenseSummaryResponse?.[0], INSIGHTS_TYPE.EXPENSES)
  }, [expenseSummaryResponse])

  const formattedProfit = useMemo(() => {
    return formatSummary(profitSummaryResponse?.[0], INSIGHTS_TYPE.PROFIT)
  }, [profitSummaryResponse])

  if (isLoading) {
    return <Loader active />
  }

  return (
    <div className="flex flex-col md:flex-row flex-wrap gap-4 w-full">
      <div className="w-full md:flex-1 md:min-w-fit">
        <InsightOverviewCard
          headerText={formattedIncome?.month_total || '-'}
          headerColor="MOSS"
          type="Income"
          percentChange={formattedIncome?.percent_change}
          month={month}
          year={year}
        />
      </div>

      <div className="w-full md:flex-1 md:min-w-fit">
        <InsightOverviewCard
          headerText={formattedExpense?.month_total || '-'}
          headerColor="YELLOW"
          type="Expenses"
          percentChange={formattedExpense?.percent_change}
          month={month}
          year={year}
        />
      </div>

      <div className="w-full md:flex-1 md:min-w-fit">
        <InsightOverviewCard
          headerText={formattedProfit?.month_total || '-'}
          type="Profit"
          headerColor="DEFAULT"
          percentChange={formattedProfit?.percent_change}
          month={month}
          year={year}
        />
      </div>
    </div>
  )
}
